import { useState } from 'react'

import {
  type Cycle,
  calculateAdjustedEndDateForCycle,
} from 'modules/core/components/DateNavigator/cycleUtils'
import { useCallback } from 'modules/core/hooks/useCallback'

export type CycleDateRange = {
  /**
   * The cycle of the date range
   */
  cycle: Cycle
  /**
   * The inclusive start date of the range
   */
  startDate: Date
  /**
   * The inclusive end date of the range
   */
  endDate: Date
}

export type CycleDateRangeWithSetters = CycleDateRange & {
  setCycle: (cycle: Cycle) => void
  setDateRange: (startDate: Date, endDate: Date) => void
}

/**
 * Hook to simplify state management of the cycle, start and end dates.
 *
 * It also adjusts the end date when the cycle or dates change, and when the hook is initialized.
 *
 * Can be used with the DateNavigator component.
 */
export const useCycleDateRange = (
  initialCycleDateRange: CycleDateRange | (() => CycleDateRange)
): CycleDateRangeWithSetters => {
  const [cycleDateRange, setCycleDateRange] = useState<CycleDateRange>(() => {
    const initialCycleDateRangeObject =
      typeof initialCycleDateRange === 'function' ? initialCycleDateRange() : initialCycleDateRange

    return {
      ...initialCycleDateRangeObject,
      endDate: calculateAdjustedEndDateForCycle(
        initialCycleDateRangeObject.startDate,
        initialCycleDateRangeObject.endDate,
        initialCycleDateRangeObject.cycle
      ),
    }
  })

  const handleCycle = useCallback((newCycle: Cycle) => {
    setCycleDateRange((prevCycleDateRange) => ({
      ...prevCycleDateRange,
      cycle: newCycle,
      endDate: calculateAdjustedEndDateForCycle(
        prevCycleDateRange.startDate,
        prevCycleDateRange.endDate,
        newCycle
      ),
    }))
  }, [])

  const handleDateRange = useCallback((newStartDate: Date, newEndDate: Date) => {
    setCycleDateRange((prevCycleDateRange) => ({
      ...prevCycleDateRange,
      startDate: newStartDate,
      endDate: calculateAdjustedEndDateForCycle(newStartDate, newEndDate, prevCycleDateRange.cycle),
    }))
  }, [])

  return {
    ...cycleDateRange,
    setCycle: handleCycle,
    setDateRange: handleDateRange,
  }
}
